/**
 * Home.js – Combined Hero and Timeline for NOadsHERE.com
 * Updated 10/06/2026
 */
import React, { Component } from "react";
import "./Home.css";

import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Card from "react-bootstrap/Card";

import { tv_details } from "../../assets/tv/tv_details";
import { movie_details } from "../../assets/movies/movie_details";

// --- Helper function to normalize all posts ---
function normalizePosts() {
    const normalizeDate = (d) => {
        if (!d) return "1900-01-01";

        const monthMap = {
            // English short
            JAN: "01",
            FEB: "02",
            MAR: "03",
            APR: "04",
            MAY: "05",
            JUN: "06",
            JUL: "07",
            AUG: "08",
            SEP: "09",
            OCT: "10",
            NOV: "11",
            DEC: "12",

            // English full
            JANUARY: "01",
            FEBRUARY: "02",
            MARCH: "03",
            APRIL: "04",
            JUNE: "06",
            JULY: "07",
            AUGUST: "08",
            SEPTEMBER: "09",
            OCTOBER: "10",
            NOVEMBER: "11",
            DECEMBER: "12",

            // Irish
            EANÁIR: "01",
            EANAIR: "01",
            FEABHRA: "02",
            MÁRTA: "03",
            MARTA: "03",
            AIBREÁN: "04",
            AIBREAN: "04",
            BEALTAINE: "05",
            MEITHEAMH: "06",
            IÚIL: "07",
            IUIL: "07",
            LÚNASA: "08",
            LUNASA: "08",
            MEÁN: "09",
            MEAN: "09",
            MEÁNFÓMHAIR: "09",
            MEANFOMHAIR: "09",
            DEIREADH: "10",
            DEIREADHFÓMHAIR: "10",
            DEIREADHFOMHAIR: "10",
            SAMHAIN: "11",
            NOLLAIG: "12"
        };

        const normalisedDate = String(d).trim();

        // Supports accented month names better than \w
        const wordPattern = "([A-Za-zÀ-ÿ]{3,20})";

        // 1. "13 MAY 25", "02 MEITHEAMH 25"
        let match = normalisedDate.match(new RegExp(`^(\\d{1,2}) ${wordPattern} (\\d{2})$`, "i"));
        if (match) {
            const day = match[1].padStart(2, "0");
            const month = monthMap[match[2].toUpperCase()] || "01";
            const year = "20" + match[3];
            return `${year}-${month}-${day}`;
        }

        // 2. "24 MAY 2025", "02 MEITHEAMH 2025"
        match = normalisedDate.match(new RegExp(`^(\\d{1,2}) ${wordPattern} (\\d{4})$`, "i"));
        if (match) {
            const day = match[1].padStart(2, "0");
            const month = monthMap[match[2].toUpperCase()] || "01";
            const year = match[3];
            return `${year}-${month}-${day}`;
        }

        // 3. "14/July/23", "07/August/22", "07/Meitheamh/25"
        match = normalisedDate.match(new RegExp(`^(\\d{1,2})/${wordPattern}/(\\d{2,4})$`, "i"));
        if (match) {
            const day = match[1].padStart(2, "0");
            const month = monthMap[match[2].toUpperCase()] || "01";
            let year = match[3];

            if (year.length === 2) {
                year = year > "30" ? "19" + year : "20" + year;
            }

            return `${year}-${month}-${day}`;
        }

        // 4. "15th Meitheamh '21"
        match = normalisedDate.match(new RegExp(`^(\\d{1,2})[a-z]{2} ${wordPattern} '?(\\d{2,4})$`, "i"));
        if (match) {
            const day = match[1].padStart(2, "0");
            const month = monthMap[match[2].toUpperCase()] || "01";
            let year = match[3];

            if (year.length === 2) {
                year = year > "30" ? "19" + year : "20" + year;
            }

            return `${year}-${month}-${day}`;
        }

        // 5. "31/01/2022" DD/MM/YYYY
        match = normalisedDate.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (match) {
            const day = match[1].padStart(2, "0");
            const month = match[2].padStart(2, "0");
            const year = match[3];
            return `${year}-${month}-${day}`;
        }

        // 6. "YYYY-MM-DD" or "YYYY/MM/DD"
        if (/^\d{4}[-/]\d{2}[-/]\d{2}$/.test(normalisedDate)) {
            return normalisedDate.replace(/\//g, "-");
        }

        console.warn("Unparsed date format:", d);
        return "1900-01-01";
    };

    const tv = tv_details.map((e) => ({
        type: "tv",
        name: e.name,
        summary: e.summary || e.heroText || "",
        date: normalizeDate(e.date),
        image: e.image_1?.image || "",
        url: "/tv/" + (e.tv || e.name)
    }));

    const movies = movie_details.map((m) => ({
        type: "movie",
        name: m.name,
        summary: m.summary || m.heroText || "",
        date: normalizeDate(m.date),
        image: m.image_1?.image || m.cover?.image || "",
        url: "/movies/" + (m.movie || m.name)
    }));

    return [...tv, ...movies].filter((p) => p.date && p.image);
}

export default class Home extends Component {
    constructor(props) {
        super(props);

        const allPosts = normalizePosts()
            .sort((a, b) => new Date(b.date) - new Date(a.date))
            .slice(0, 20);

        this.state = {
            allPosts,
            loadedImages: {}
        };
    }

    componentDidMount() {
        this.deleteAllCookies();

        this.state.allPosts.forEach((item) => {
            import("../../assets/images/" + item.image)
                .then((image) => {
                    this.setState((prevState) => ({
                        loadedImages: {
                            ...prevState.loadedImages,
                            [item.image]: image.default
                        }
                    }));
                })
                .catch((err) => console.error("Error loading image:", err));
        });
    }

    deleteAllCookies() {
        const cookies = document.cookie.split(";");

        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i];
            const eqPos = cookie.indexOf("=");
            const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;

            document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT";
        }

        localStorage.clear();
        sessionStorage.clear();
    }

    render() {
        if (!this.state.allPosts.length) {
            return <div>Loading...</div>;
        }

        return (
            <div className="divmor">
                <div className="prideBannerTop"></div>
                <p className="mobileHomeTagline">independent movie &amp; tv review site with zero ads or tracking</p>

                <Container className="homeContainer">
                    <Row className="postsRow g-0">
                        {this.state.allPosts.map((item) => (
                            <Col
                                className="tvCol"
                                xs={12}
                                md={4}
                                lg={4}
                                key={`${item.type}-${item.name}`}
                            >
                                <div
                                    className="bgImageDiv"
                                    style={{
                                        backgroundImage: this.state.loadedImages[item.image]
                                            ? `url(${this.state.loadedImages[item.image]})`
                                            : "none"
                                    }}
                                >
                                    <Card className="contentCard tv">
                                        <a className="cardLink" href={item.url}>
                                            <p className="cardLinkText">
                                                {item.name}
                                                <span className="postTypeBadge">
                                                    {item.type.toUpperCase()}
                                                </span>
                                            </p>
                                        </a>
                                    </Card>
                                </div>

                                <p className="tileText">{item.summary}</p>
                            </Col>
                        ))}
                    </Row>
                </Container>

                <div className="prideBannerBottom"></div>

                <div className="footerAbout">
                    <a className="footerLinks" href="/">home</a>
                    <a className="footerLinks" href="/tv/all">tv</a>
                    <a className="footerLinks" href="/movies/all">movies</a>
                    <a className="footerLinks" href="/about/">about</a>
                </div>
            </div>
        );
    }
}
